/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';

import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import SettingsService from 'cio/services/settings';
import EnvironmentService from 'cio/services/environment';
import EnvironmentModel from 'cio/models/environment';
import { IdentifierType } from 'cio/utils/identifiers';
import ConfirmChangesService from 'cio/services/confirm-changes';

export default class LanguageSettingsPage extends Component {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service account!: AccountService;
  @service environment!: EnvironmentService;
  @service settings!: SettingsService;
  @service confirmChanges!: ConfirmChangesService;

  @tracked showDisableConfirmationModal = false;
  @tracked showLanguageAttributeFeedbackForm = false;

  peopleTalkingImage = new URL('cio/assets/images/illustrations/people-talking.png', import.meta.url).toString();

  @computed('currentEnvironment.hasDirtyAttributes')
  get isDirty() {
    return this.currentEnvironment.hasDirtyAttributes;
  }

  get currentEnvironment() {
    return this.environment.current;
  }

  get canDisable() {
    let environment = this.currentEnvironment;
    return isPresent(environment?.languageAttribute) && !(environment as EnvironmentModel).hasDirtyAttributes;
  }

  get isDisabled() {
    return !this.auth.hasPermission('journeys/language_settings/edit') || this.saveTask.isRunning;
  }

  saveTask = task(async () => {
    let environment = this.currentEnvironment;

    try {
      if (environment.identifierType !== IdentifierType.MultiIdentifier) {
        (environment as any).identifiers = null;
      }

      await environment.save();

      this.alerts.success('environment.update.success', 'Your language settings were updated');
    } catch {
      environment.rollbackAttributes();

      this.alerts.error(
        'environment.update.success',
        'Unable to update your language settings. Please contact support or try again.',
      );
    }
  });

  disableLocalizationTask = task(async () => {
    this.currentEnvironment.languageAttribute = '';
    await this.saveTask.perform();
    this.showDisableConfirmationModal = false;
  });

  @action
  setLanguageAttribute({ value }: { value: string }) {
    this.currentEnvironment.languageAttribute = value;
  }

  @action
  resetEnvironment() {
    this.currentEnvironment.rollbackAttributes();
  }

  @action
  trackLinkClick(e: Event) {
    this.analytics.track('languages', 'settings_tip_link_clicked', {
      languageAttribute: this.currentEnvironment.languageAttribute,
      link: (e.target as HTMLAnchorElement).href,
    });
  }

  @action
  setupConfirmChanges() {
    this.confirmChanges.register(this);
  }
}
